import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Modal from "@/components/base/Modal";
import { useApi } from "@/hooks/useApi";
import type { CardMember } from "@/lib/types";
import { Skeleton } from "@/components/base/Skeleton";
import { validColor } from "@/lib/color";
import { mediaUrl } from "@/lib/media";
import { careerPillClass } from "./MemberCard";

interface MemberCardModalProps {
  no: number | null;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}

// 명함 상세 모달 (경력 전체 + 앞/뒤 전환 + 지도 버튼)
export default function MemberCardModal({
  no,
  onClose,
  onPrev,
  onNext,
}: MemberCardModalProps) {
  const { data, loading, error } = useApi<CardMember | CardMember[]>(
    no != null ? `cards.php?no=${no}` : null
  );
  const [side, setSide] = useState<"front" | "back">("front");

  const detail: CardMember | null = Array.isArray(data) ? data[0] ?? null : data;

  useEffect(() => {
    setSide("front");
  }, [no]);

  const front = detail?.card_img1 || "";
  const back = detail?.card_img2 || "";
  const hasBoth = Boolean(front && back);
  const shown = side === "back" ? back : front;
  const hp = String(detail?.hp ?? "").trim();
  const join = String(detail?.join_date ?? "").trim();
  const birth = String(detail?.birth ?? "").trim();
  const cate = detail?.cate_name || detail?.biz_type || "";
  const cateColor = validColor(detail?.cate_color) || "#17408B";

  return (
    <Modal open={no != null} onClose={onClose} maxWidth="max-w-lg">
      <div className="relative p-5 md:p-6">
        <button
          type="button"
          onClick={onClose}
          aria-label="닫기"
          className="absolute right-4 top-4 z-10 flex h-8 w-8 items-center justify-center rounded-full text-foreground-500 hover:bg-background-100"
        >
          <i className="ri-close-line text-xl" aria-hidden="true"></i>
        </button>

        {loading ? (
          <div className="space-y-4 pt-6">
            <Skeleton className="h-6 w-40" />
            <Skeleton className="h-48 w-full rounded-lg" />
            <Skeleton className="h-4 w-2/3" />
          </div>
        ) : error || !detail ? (
          <p className="py-10 text-center text-sm text-foreground-500">
            명함 정보를 불러오지 못했습니다.
          </p>
        ) : (
          <>
            <div className="pr-10">
              <div className="flex items-baseline gap-2">
                {detail.aho && (
                  <span className="text-xs text-foreground-500">{detail.aho}</span>
                )}
                <h2 className="font-heading text-xl font-extrabold text-primary-700">
                  {detail.name}
                </h2>
                {detail.biz_position && (
                  <span className="rounded bg-primary-600 px-1.5 py-0.5 text-[11px] text-background-50">
                    {detail.biz_position}
                  </span>
                )}
              </div>
            </div>

            {shown ? (
              <div className="mt-5">
                <div className="relative w-full overflow-hidden rounded-lg border border-background-200 bg-background-100">
                  <div className="relative aspect-[9/5] w-full">
                    <img
                      src={mediaUrl(shown)}
                      alt={`${detail.name} 명함 ${side === "back" ? "뒷면" : "앞면"}`}
                      className="h-full w-full object-contain"
                    />
                  </div>
                </div>
                {hasBoth && (
                  <div className="mt-3 inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
                    <button
                      type="button"
                      onClick={() => setSide("front")}
                      className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                        side === "front"
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600"
                      }`}
                    >
                      앞면
                    </button>
                    <button
                      type="button"
                      onClick={() => setSide("back")}
                      className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                        side === "back"
                          ? "bg-primary-500 text-background-50"
                          : "text-foreground-600"
                      }`}
                    >
                      뒷면
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-5 flex h-32 items-center justify-center rounded-lg border border-dashed border-background-300 text-sm text-foreground-400">
                등록된 명함 이미지가 없습니다
              </div>
            )}

            {(detail.biz_name || cate) && (
              <div className="mt-5 flex flex-wrap items-center gap-2.5 rounded-lg bg-background-100 p-4">
                {detail.biz_name && (
                  <span className="font-heading font-bold text-foreground-900">
                    {detail.biz_name}
                  </span>
                )}
                {cate && (
                  <span
                    className="whitespace-nowrap rounded-full border px-2.5 py-[1px] text-[12px]"
                    style={{ borderColor: cateColor, color: cateColor }}
                  >
                    {cate}
                  </span>
                )}
              </div>
            )}

            {detail.career && detail.career.length > 0 && (
              <div className="mt-4 flex flex-col items-start gap-1.5">
                {detail.career.map((c, i) => (
                  <span
                    key={i}
                    className={`inline-block max-w-full whitespace-normal break-keep rounded-md px-3 py-1.5 text-[13px] leading-snug ${careerPillClass(c)}`}
                  >
                    {c}
                  </span>
                ))}
              </div>
            )}

            {(join || birth) && (
              <div className="mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-foreground-500">
                {join && <span>입회: {join}</span>}
                {birth && <span>생일: {birth}</span>}
              </div>
            )}

            <div className="mt-5 flex items-center gap-2">
              {hp && (
                <a
                  href={`tel:${hp.replace(/[^0-9+]/g, "")}`}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-secondary-600"
                >
                  <i className="ri-phone-line" aria-hidden="true"></i>
                  전화
                </a>
              )}
              {detail.has_map === 1 && (
                <Link
                  to={`/bizmap?no=${detail.no}`}
                  onClick={onClose}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-map-pin-line" aria-hidden="true"></i>
                  지도에서 보기
                </Link>
              )}
            </div>

            {detail.is_mine === 1 && (
              <Link
                to="/my/profile"
                onClick={onClose}
                className="mt-3 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-primary-300 bg-primary-50 px-4 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100"
              >
                <i className="ri-edit-line" aria-hidden="true"></i>
                내 명함 수정
              </Link>
            )}
          </>
        )}

        {/* 이전 / 다음 */}
        <button
          type="button"
          onClick={onPrev}
          aria-label="이전 명함"
          className="absolute left-1 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 sm:flex"
        >
          <i className="ri-arrow-left-s-line text-xl" aria-hidden="true"></i>
        </button>
        <button
          type="button"
          onClick={onNext}
          aria-label="다음 명함"
          className="absolute right-1 top-1/2 hidden h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-background-200 sm:flex"
        >
          <i className="ri-arrow-right-s-line text-xl" aria-hidden="true"></i>
        </button>
      </div>
    </Modal>
  );
}